'use client';

import { FC, useEffect, useContext, useState } from 'react';
import { ClientTagsDialogProps } from './ClientTagsDialog.types';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Icon,
  Text,
} from '@/_components';
import styles from './ClientTagsDialog.module.css';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { addClientTag, removeClientTag } from '@/_api/admin-tags';
import { AddClientTagForm } from './components/AddClientTagForm';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';

export const ClientTagsDialog: FC<ClientTagsDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [isAdding, setAdding] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [deletingTagId, setDeletingTagId] = useState<number | undefined>(
    undefined,
  );

  const { token } = useContext(UserContext);
  const { firm, reloadFirm } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);

  const onToggleAdding = () => setAdding(!isAdding);
  const hasClientTag: boolean = !!firm?.contact_tags?.length;

  const onConfirm = async (newTag: string) => {
    try {
      setLoading(true);
      setAdding(false);

      const { error } = await addClientTag({ new_tag: newTag, token });

      if (error) {
        addToast('error', error, { error });
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      reloadFirm();
      setLoading(false);
    }
  };

  const onRemoveTag = async (tagId: number) => {
    try {
      setDeletingTagId(tagId);
      await removeClientTag({ tag_id: tagId, token });
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      reloadFirm();
      setDeletingTagId(undefined);
    }
  };

  useEffect(() => {
    return () => setAdding(false);
  }, [isOpen]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="User Tags">
      <Dialog.Content>
        {isAdding ? (
          <AddClientTagForm onCancel={onToggleAdding} onConfirm={onConfirm} />
        ) : (
          <Flex isColumn={true} gap={8}>
            {!hasClientTag && (
              <Alert type="info" icon="info">
                No user tags have been created yet.
              </Alert>
            )}
            <Flex isWrapping={true} gap={4}>
              {firm?.contact_tags?.map((tag, key) => {
                const onDelete = () => onRemoveTag(tag.id);

                return (
                  <Flex
                    key={key}
                    isBetween={true}
                    gap={4}
                    className={styles.clientTag}
                  >
                    <Flex isYCentered={true} gap={4}>
                      <Icon name="tag" size={16} />
                      <Text type="small" fontWeight="medium">
                        {tag.name}
                      </Text>
                    </Flex>
                    <ButtonGroup>
                      <Button
                        intent="danger-secondary"
                        iconLeft="trash"
                        isDisabled={deletingTagId === tag.id}
                        isLoading={deletingTagId === tag.id}
                        value="Remove Tag"
                        isIconOnly={true}
                        size="tiny"
                        onClick={onDelete}
                      />
                    </ButtonGroup>
                  </Flex>
                );
              })}
            </Flex>
            <ButtonGroup align="right">
              <Button
                value="New User tag"
                onClick={onToggleAdding}
                isDisabled={isLoading}
                isLoading={isLoading}
                intent={hasClientTag ? 'secondary' : 'primary'}
                iconLeft="plus"
              />
            </ButtonGroup>
          </Flex>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
